import i18next from 'i18next';

import { isNumber } from '../utils/validations';
import { applyThousandsSeparator } from '../utils/strings';
import { formatUTCHour, getHoursOffset } from '../utils/date';
import { roundThreeDecimals, roundTwoDecimals, weightUnits } from '../config/commons';
import { ParameterDefault, TransferMetric, Tray as ITray, Parameter, Transport, TransferTransport } from '../pages/Sowings/WeighingTransfers/interfaces';


class Tray {
  trayWeight: number;
  averageWeight?: number;
  uniformityWeight?: number;
  variationCoefficientWeight?: number;
  smallAnimalsPercentage?: number;

  constructor (props: { trayWeight: number; sampleWeight?: number; animals?: number; smallAnimals?: number; uniformityWeight?: number; variationCoefficientWeight?: number; }) {
    const { trayWeight, sampleWeight, animals, smallAnimals, uniformityWeight, variationCoefficientWeight } = props;

    this.trayWeight = trayWeight;
    this.averageWeight = sampleWeight !== undefined && animals !== undefined ? sampleWeight / animals : undefined;
    this.smallAnimalsPercentage = sampleWeight !== undefined && smallAnimals !== undefined ? smallAnimals / sampleWeight : undefined;
    this.uniformityWeight = uniformityWeight;
    this.variationCoefficientWeight = variationCoefficientWeight;
  }
}

class WeightedCalculator<T> {
  trays: Tray[];
  totalWeight: number;
  weightedTotal: number;
  noSampleGroupWeight: number;
  lastKnownValue?: number;
  // eslint-disable-next-line
  getValue: (tray: Tray) => T | undefined;

  constructor (getValue: (tray: Tray) => T | undefined) {
    this.trays = [];
    this.totalWeight = 0;
    this.weightedTotal = 0;
    this.noSampleGroupWeight = 0;
    this.lastKnownValue = undefined;
    this.getValue = getValue; // Function to get specific value from Tray
  }

  addTray (props: { trayWeight: number; sampleWeight?: number; animals?: number; smallAnimals?: number; uniformityWeight?: number; variationCoefficientWeight?: number; }): number | string {
    const { trayWeight, sampleWeight, animals, smallAnimals, uniformityWeight, variationCoefficientWeight } = props;

    const tray = new Tray({ trayWeight, sampleWeight, animals, smallAnimals, uniformityWeight, variationCoefficientWeight });
    this.trays.push(tray);
    this.totalWeight += trayWeight;

    const value = this.getValue(tray);

    if (value === undefined) {
      // If no value, accumulate weight in the no-sample group
      this.noSampleGroupWeight += trayWeight;

      if (this.trays.every(d => this.getValue(d) === undefined)) {
        // No values so far, return 'N/A'
        return '-';
      }

      // Use the last known value if no sample is in this tray
      return this.lastKnownValue === undefined ? '-' : this.lastKnownValue;
    }

    // If this is the first sample, initialize weighted total with the tray's value
    if (this.trays.filter(d => this.getValue(d) !== undefined).length === 1) {
      this.weightedTotal = Number(value) * this.totalWeight;
    } else {
      // For subsequent samples, include the no-sample group in the weighting
      this.weightedTotal += Number(value) * (this.noSampleGroupWeight + trayWeight);
    }

    // Reset the no-sample group weight after a sample
    this.noSampleGroupWeight = 0;

    // Calculate the current weighted value
    const currentWeightedValue = this.weightedTotal / this.totalWeight;
    this.lastKnownValue = currentWeightedValue; // Save the last known value
    return currentWeightedValue;
  }
}

export const transportTypes = {
  CAPSULE: 'CAPSULE',
  PUMP: 'PUMP',
};

export const transportParameters = {
  STOCKING_DESTINATION: 'stockingDestination',
  INITIAL_HOUR: 'initialHour',
  FINAL_HOUR: 'finalHour',
  INITIAL_OXYGEN: 'initialOxygen',
  FINAL_OXYGEN: 'finalOxygen',
  INITIAL_TEMPERATURE: 'initialTemperature',
  FINAL_TEMPERATURE: 'finalTemperature',
  INITIAL_SATURATION: 'initialSaturation',
  FINAL_SATURATION: 'finalSaturation',
};

export const defaultCapsuleParameters: ParameterDefault[] = [
  { key: 'averageWeight', value: 0 },
  { key: 'uniformityWeight', value: 0 },
  { key: 'variationCoefficientWeight', value: 0 },
  { key: 'transferredInCapsule', value: 0 },
  { key: 'animalsInCapsule', value: 0 },
  { key: 'animalsPerCubicMeter', value: 0 },
  { key: 'animalsSmallerThan', value: 0 },
];

export const defaultPumpParameters: ParameterDefault[] = [
  { key: 'averageWeight', value: 0 },
  { key: 'uniformityWeight', value: 0 },
  { key: 'variationCoefficientWeight', value: 0 },
  { key: 'transferredInPump', value: 0 },
  { key: 'animalsInPump', value: 0 },
  { key: 'animalsPerCubicMeter', value: 0 },
  { key: 'animalsSmallerThan', value: 0 },
];


export const getParameterLabel = (props: { parameter: ParameterDefault; maxSmallAnimalsWeight: number }) => {
  const { parameter, maxSmallAnimalsWeight } = props;

  switch (parameter.key) {
    case 'animalsSmallerThan':
      return i18next.t(`stockings.transfers.transportParameters.${parameter.key}`, { value: maxSmallAnimalsWeight }) + '' + weightUnits.G;

    default:
      return i18next.t(`stockings.transfers.transportParameters.${parameter.key}`);
  }
};

// eslint-disable-next-line max-lines-per-function
export const getFormattedParameterValue = (props: { parameter: ParameterDefault; trays: ITray[]; transferMetric: TransferMetric }) => {
  const { parameter, trays, transferMetric } = props;
  const { averageWeight, animalsTransferred, animalsPerCubicMeter, smallAnimalsPercentage, uniformityWeight, variationCoefficientWeight } = transferMetric;

  switch (parameter.key) {
    case 'finalTemperature':
    case 'initialTemperature':
      return `${parameter.value}°`;

    case 'finalOxygen':
    case 'initialOxygen':
      return `${parameter.value} ppm`;

    case 'finalSaturation':
    case 'initialSaturation':
      return `${parameter.value}%`;

    case 'finalHour':
    case 'initialHour': {
      const hoursOffset = getHoursOffset();
      return formatUTCHour(parameter.value.toString(), hoursOffset);
    }

    case 'averageWeight': {
      return `${averageWeight} ${weightUnits.G}`;
    }

    case 'uniformityWeight': {
      return `${uniformityWeight}%`;
    }

    case 'variationCoefficientWeight': {
      return `${variationCoefficientWeight}%`;
    }

    case 'animalsPerCubicMeter': {
      return `${animalsPerCubicMeter} juv/m³`;
    }

    case 'animalsSmallerThan': {
      return `${smallAnimalsPercentage}%`;
    }

    case 'animalsInCapsule':
    case 'animalsInPump': {
      return `${applyThousandsSeparator(animalsTransferred)} juv`;
    }

    case 'transferredInCapsule':
    case 'transferredInPump': {
      const kilogramsTransferred = calcKilogramsTransferred({ trays });
      return `${applyThousandsSeparator(kilogramsTransferred)} ${weightUnits.KG}`;
    }

    default:
      return `${parameter.value}`;
  }
};

export const getDefaultParameterValue = (props: { parameter: ParameterDefault; trays: ITray[]; transferMetric: TransferMetric }) => {
  const { parameter, trays, transferMetric } = props;
  const { averageWeight, animalsTransferred, animalsPerCubicMeter, smallAnimalsPercentage, uniformityWeight, variationCoefficientWeight } = transferMetric;

  switch (parameter.key) {
    case 'averageWeight': {
      return isNumber(averageWeight) ? Number(averageWeight) : undefined;
    }

    case 'uniformityWeight': {
      return isNumber(uniformityWeight) ? Number(uniformityWeight) : undefined;
    }

    case 'variationCoefficientWeight': {
      return isNumber(variationCoefficientWeight) ? Number(variationCoefficientWeight) : undefined;
    }

    case 'animalsPerCubicMeter': {
      return isNumber(animalsPerCubicMeter) ? Number(animalsPerCubicMeter) : undefined;
    }

    case 'animalsSmallerThan': {
      return isNumber(smallAnimalsPercentage) ? Number(smallAnimalsPercentage) : undefined;
    }

    case 'animalsInCapsule':
    case 'animalsInPump': {
      return isNumber(animalsTransferred) ? Number(animalsTransferred) : undefined;
    }

    case 'transferredInCapsule':
    case 'transferredInPump': {
      const kilogramsTransferred = calcKilogramsTransferred({ trays });
      return isNumber(kilogramsTransferred) ? Number(kilogramsTransferred) : undefined;
    }
  }
};

export const showErrorParameterValue = (props: { parameter: ParameterDefault; smallAnimalsPercentage: number | string; maxSmallAnimalsPercentage: number }) => {
  const { parameter, smallAnimalsPercentage, maxSmallAnimalsPercentage } = props;

  switch (parameter.key) {
    case 'animalsSmallerThan': {
      if (!isNumber(smallAnimalsPercentage)) {
        return false;
      }

      return Number(smallAnimalsPercentage) > maxSmallAnimalsPercentage;
    }

    default:
      return false;
  }
};

const calcKilogramsTransferred = (props: { trays: ITray[] }) => {
  const { trays } = props;
  const kilogramsTransferred = trays.reduce((total, tray) => total + tray.trayWeight, 0);
  return roundTwoDecimals(kilogramsTransferred);
};

// eslint-disable-next-line max-lines-per-function
export const calculateTransferMetrics = (props: { trays: ITray[]; maxSmallAnimalsWeight: number; volume?: number }): TransferMetric => {
  const { trays, volume } = props;

  let averageWeight: string | number = 0;
  let uniformityWeight: string | number = 0;
  let variationCoefficientWeight: string | number = 0;
  let smallAnimalsPercentage: string | number = 0;

  const averageCalculator = new WeightedCalculator<number>((tray) => tray.averageWeight);
  const uniformityCalculator = new WeightedCalculator<number>((tray) => tray.uniformityWeight);
  const variationCoefficientCalculator = new WeightedCalculator<number>((tray) => tray.variationCoefficientWeight);
  const smallAnimalsPercentageCalculator = new WeightedCalculator<number>((tray) => tray.smallAnimalsPercentage);

  for (let index = 0; index < trays.length; index++) {
    const tray = trays[index];
    const trayWeight = tray.trayWeight * 1000;

    averageWeight = averageCalculator.addTray({ trayWeight, sampleWeight: tray.sampleWeight, animals: tray.animals });
    uniformityWeight = uniformityCalculator.addTray({ trayWeight, uniformityWeight: tray.uniformityWeight });
    variationCoefficientWeight = variationCoefficientCalculator.addTray({ trayWeight, variationCoefficientWeight: tray.variationCoefficientWeight });

    let smallAnimals = 0;
    if (tray.sampleWeight && tray.individualWeights && tray.individualWeights.length > 0) {
      smallAnimals = tray.individualWeights.filter((num: number) => num < props.maxSmallAnimalsWeight).length;
    }
    
    smallAnimalsPercentage = smallAnimalsPercentageCalculator.addTray({ trayWeight, sampleWeight: tray.sampleWeight, smallAnimals });
  }

  if (!isNumber(averageWeight) || !isNumber(smallAnimalsPercentage) || averageWeight === 0) {
    return {
      averageWeight: 0,
      animalsTransferred: 0,
      animalsPerCubicMeter: 0,
      smallAnimalsPercentage: 0,
      uniformityWeight: 0,
      variationCoefficientWeight: 0,
    };
  }

  averageWeight = Number(averageWeight);
  uniformityWeight = Number(uniformityWeight);
  variationCoefficientWeight = Number(variationCoefficientWeight);
  smallAnimalsPercentage = Number(smallAnimalsPercentage);

  const kilogramsTransferred = calcKilogramsTransferred({ trays });
  const animalsTransferred = Math.round(kilogramsTransferred * 1000 / averageWeight);
  
  return {
    averageWeight: roundThreeDecimals(averageWeight),
    uniformityWeight: isNumber(uniformityWeight) ? roundTwoDecimals(uniformityWeight) : 0,
    variationCoefficientWeight: isNumber(variationCoefficientWeight) ? roundTwoDecimals(variationCoefficientWeight) : 0,
    animalsTransferred,
    animalsPerCubicMeter: volume && volume > 0 ? Math.round(animalsTransferred / volume) : 0,
    smallAnimalsPercentage: roundTwoDecimals(smallAnimalsPercentage),
  };
};

export const calculateTransferMetricByTransports = (props: { trays: ITray[]; maxSmallAnimalsWeight: number; transports: Transport[] }): TransferMetric[] => {
  const { trays, maxSmallAnimalsWeight, transports } = props;

  const transferMetricByTransports: TransferMetric[] = [];

  for (let i = 0; i < transports.length; i++) {
    const transport = transports[i];
    const traysFiltered = trays.filter((tray) => tray.transportId === transport._id);

    const transferMetric = calculateTransferMetrics({ trays: traysFiltered, maxSmallAnimalsWeight, volume: transport.capacity });
    const transferMetricByTransport: TransferMetric = transferMetric;

    transferMetricByTransports.push(transferMetricByTransport);
  }

  return transferMetricByTransports;
};

export const getGlobalSmallAnimalsPercentage = (props: { transferMetrics: TransferMetric[] }) => {
  const { transferMetrics } = props;

  const totalAnimalsTransferred = transferMetrics.reduce((total, transferMetric) => total + (isNumber(transferMetric.animalsTransferred) ? Number(transferMetric.animalsTransferred) : 0), 0);
  let weightedTotal = 0;

  for (let i = 0; i < transferMetrics.length; i++) {
    const transferMetric = transferMetrics[i];

    if (!isNumber(transferMetric.smallAnimalsPercentage) || !isNumber(transferMetric.animalsTransferred)) {
      continue;
    }

    weightedTotal += (Number(transferMetric.smallAnimalsPercentage) * Number(transferMetric.animalsTransferred));
  }

  if (weightedTotal === 0 || totalAnimalsTransferred === 0) {
    return 0;
  }

  return roundTwoDecimals(weightedTotal / totalAnimalsTransferred);
};

export const getParameterKeys = (props: { parameters: Parameter[] }) => {
  const { parameters } = props;

  const transportParameterValues = Object.values(transportParameters);
  const parameterKeys = new Set(parameters.map((parameter) => parameter.key.toString()));

  return transportParameterValues.filter((value) => {
    switch (value) {
      case transportParameters.FINAL_HOUR:
        return !parameterKeys.has(transportParameters.FINAL_HOUR) &&
          parameterKeys.has(transportParameters.INITIAL_HOUR);
      case transportParameters.FINAL_OXYGEN:
        return !parameterKeys.has(transportParameters.FINAL_OXYGEN) &&
          parameterKeys.has(transportParameters.INITIAL_OXYGEN);
      case transportParameters.FINAL_TEMPERATURE:
        return !parameterKeys.has(transportParameters.FINAL_TEMPERATURE) &&
          parameterKeys.has(transportParameters.INITIAL_TEMPERATURE);
      case transportParameters.FINAL_SATURATION:
        return !parameterKeys.has(transportParameters.FINAL_SATURATION) &&
          parameterKeys.has(transportParameters.INITIAL_SATURATION);
      default:
        return !parameterKeys.has(value);
    }
  });
};

export const isTransferTypeCapsule = (transports: TransferTransport[]) => {
  if (!transports?.length) {
    return false;
  }

  const transferTransport = transports[0];
  return transferTransport.transportType === transportTypes.CAPSULE;
};
